import React, { useState } from 'react';
import { ArrowLeft, Bell, AlertTriangle, History, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';

interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'alert' | 'transaction' | 'system';
  time: string;
  unread: boolean;
}

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: 1,
      title: 'Low Stock Alert',
      message: 'Wireless Headphones are running low on stock',
      type: 'alert',
      time: '5 minutes ago',
      unread: true,
    },
    {
      id: 2,
      title: 'New Transaction',
      message: 'Stock adjustment completed for Office Chair',
      type: 'transaction',
      time: '1 hour ago',
      unread: false,
    },
    {
      id: 3,
      title: 'System Update',
      message: 'New features have been added to the dashboard',
      type: 'system',
      time: '2 hours ago',
      unread: false,
    },
    {
      id: 4,
      title: 'Low Stock Alert',
      message: 'Smart Watch stock is below minimum level',
      type: 'alert',
      time: '3 hours ago',
      unread: false,
    },
    {
      id: 5,
      title: 'New Transaction',
      message: 'Incoming stock received for Cotton T-Shirt',
      type: 'transaction',
      time: '4 hours ago',
      unread: false,
    },
  ]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'alert':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'transaction':
        return <History className="h-5 w-5 text-blue-500" />;
      case 'system':
        return <Settings className="h-5 w-5 text-purple-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, unread: false })));
  };

  const markAsRead = (id: number) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, unread: false } : n
    ));
  };

  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          {unreadCount > 0 && (
            <Badge variant="primary" className="ml-2">
              {unreadCount} unread
            </Badge>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="secondary" onClick={markAllAsRead}>
            Mark all as read
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Notifications</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`py-4 ${notification.unread ? 'bg-blue-50' : ''}`}
                onClick={() => markAsRead(notification.id)}
              >
                <div className="flex items-start gap-3 px-4">
                  <div className="p-2 bg-white rounded-full shadow-sm">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">
                        {notification.title}
                        {notification.unread && (
                          <Badge variant="primary" className="ml-2">New</Badge>
                        )}
                      </p>
                      <span className="text-xs text-gray-500">{notification.time}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{notification.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;